import { RegionId } from '@maco144/shared';
import { ChatMessage, ChatReaction, ChannelSummary } from '@maco144/shared';
import { MessageStore, MessageQuery } from '@maco144/shared';

// IndexedDB message store for browser
export class IndexedDBMessageStore implements MessageStore {
  private dbName: string;
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  /**
   * Create a message store for a specific identity
   * If userId is provided, database is namespaced to that identity
   * Otherwise, uses default database name for backwards compatibility
   */
  constructor(userIdOrDbName: string = 'rising-sun-chat') {
    // If the string looks like a hash (starts with hex), treat as userId
    // Otherwise treat as explicit database name for backwards compatibility
    if (/^[a-f0-9]+$/i.test(userIdOrDbName) && userIdOrDbName.length > 20) {
      this.dbName = `rising-sun-chat-${userIdOrDbName}`;
    } else {
      this.dbName = userIdOrDbName;
    }
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Messages store
        if (!db.objectStoreNames.contains('messages')) {
          const store = db.createObjectStore('messages', { keyPath: 'id' });
          store.createIndex('regionId', 'regionId', { unique: false });
          store.createIndex('sender', 'sender', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('region_timestamp', ['regionId', 'timestamp'], { unique: false });
        }

        // Reactions store
        if (!db.objectStoreNames.contains('reactions')) {
          const store = db.createObjectStore('reactions', {
            keyPath: ['messageId', 'sender', 'emoji'],
          });
          store.createIndex('messageId', 'messageId', { unique: false });
        }
      };
    });
  }

  private getStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const tx = this.db.transaction(storeName, mode);
    return tx.objectStore(storeName);
  }

  async save(message: ChatMessage): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readwrite');
      const request = store.put(message);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async get(id: string): Promise<ChatMessage | undefined> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readonly');
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async query(q: MessageQuery): Promise<ChatMessage[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readonly');
      const results: ChatMessage[] = [];

      let request: IDBRequest;

      if (q.regionId) {
        const index = store.index('region_timestamp');
        const range = IDBKeyRange.bound(
          [q.regionId, q.since ?? 0],
          [q.regionId, q.until ?? Number.MAX_SAFE_INTEGER]
        );
        request = index.openCursor(range);
      } else {
        request = store.openCursor();
      }

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue | null;

        if (cursor) {
          const msg = cursor.value as ChatMessage;

          // Apply additional filters
          let include = true;
          if (q.sender && msg.sender !== q.sender) include = false;
          if (!q.regionId) {
            if (q.since && msg.timestamp < q.since) include = false;
            if (q.until && msg.timestamp > q.until) include = false;
          }

          if (include) results.push(msg);
          cursor.continue();
        } else {
          // Sort by timestamp
          results.sort((a, b) => a.timestamp - b.timestamp);

          // Paginate
          let paginated = results;
          if (q.offset) {
            paginated = paginated.slice(q.offset);
          }
          if (q.limit) {
            paginated = paginated.slice(0, q.limit);
          }

          resolve(paginated);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async delete(id: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readwrite');
      const request = store.delete(id);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getReactions(messageId: string): Promise<ChatReaction[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('reactions', 'readonly');
      const index = store.index('messageId');
      const request = index.getAll(messageId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async saveReaction(reaction: ChatReaction): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('reactions', 'readwrite');
      const request = store.put(reaction);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSummary(regionId: RegionId): Promise<ChannelSummary> {
    const messages = await this.query({ regionId });

    if (messages.length === 0) {
      return { regionId, messageCount: 0 };
    }

    return {
      regionId,
      messageCount: messages.length,
      oldestTimestamp: messages[0].timestamp,
      newestTimestamp: messages[messages.length - 1].timestamp,
      lastWorldRoot: messages[messages.length - 1].worldRoot,
    };
  }

  async clear(regionId?: RegionId): Promise<void> {
    if (regionId) {
      // Delete messages for specific region
      const messages = await this.query({ regionId });
      const store = this.getStore('messages', 'readwrite');

      for (const msg of messages) {
        await new Promise<void>((resolve, reject) => {
          const request = store.delete(msg.id);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    } else {
      // Clear all
      await new Promise<void>((resolve, reject) => {
        const store = this.getStore('messages', 'readwrite');
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await new Promise<void>((resolve, reject) => {
        const store = this.getStore('reactions', 'readwrite');
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  }

  // Export all messages (for backup)
  async exportAll(): Promise<ChatMessage[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('messages', 'readonly');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Import messages (merge)
  async importAll(messages: ChatMessage[]): Promise<number> {
    let count = 0;
    const store = this.getStore('messages', 'readwrite');

    for (const msg of messages) {
      const existing = await this.get(msg.id);
      if (!existing) {
        await new Promise<void>((resolve, reject) => {
          const request = store.put(msg);
          request.onsuccess = () => {
            count++;
            resolve();
          };
          request.onerror = () => reject(request.error);
        });
      }
    }

    return count;
  }

  close(): void {
    this.db?.close();
    this.db = null;
  }
}
